"use client"

import type React from "react"
import { useState, useCallback } from "react"
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner"
import { ACCOUNT_CONSTANTS } from "../../constants/account-details"
import { useToast } from "../../context/ToastContext";
import type { FormState } from "../../types/account-details"

const ProfilePhotoUpload: React.FC = () => {
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null)
  const { error, success } = useToast();
  const [formState, setFormState] = useState<FormState>({
    loading: false,
    error: null,
    success: null,
  })

  const handlePhotoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]

      // Validate file size
      if (file.size > ACCOUNT_CONSTANTS.VALIDATION.MAX_FILE_SIZE) {
        setFormState((prev) => ({ ...prev, error: ACCOUNT_CONSTANTS.MESSAGES.FILE_TOO_LARGE }))
        return
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        setFormState((prev) => ({ ...prev, error: ACCOUNT_CONSTANTS.MESSAGES.INVALID_FILE_TYPE }))
        return
      }

      setProfilePhoto(URL.createObjectURL(file))
      setFormState((prev) => ({ ...prev, error: null }))
    }
  }, [])

  const handlePhotoSubmit = useCallback(async () => {
    if (!profilePhoto) return

    setFormState({ loading: true, error: null, success: null })

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000))
      success(ACCOUNT_CONSTANTS.SUCCESS_MESSAGES.PHOTO_UPDATED)
      setFormState({
        loading: false,
        error: null,
        success: ACCOUNT_CONSTANTS.SUCCESS_MESSAGES.PHOTO_UPDATED,
      })

      // Clear success message after 3 seconds
      setTimeout(() => {
        setFormState((prev) => ({ ...prev, success: null }))
      }, 3000)
    } catch (err) {
      error(ACCOUNT_CONSTANTS.ERROR_MESSAGES.PHOTO_UPDATE_FAILED)
      setFormState({
        loading: false,
        error: ACCOUNT_CONSTANTS.ERROR_MESSAGES.PHOTO_UPDATE_FAILED,
        success: null,
      })
    }
  }, [profilePhoto])

  const handleRemovePhoto = useCallback(() => {
    setProfilePhoto(null)
    setFormState((prev) => ({ ...prev, error: null, success: null }))
  }, [])

  return (
    <div className="form-section profile-section">
      <h3 className="section-title">Profile Photo</h3>

      <div className="profile-upload-container">
        <div className="profile-upload-area">
          <label htmlFor="profile-upload" className="profile-upload-label">
            {profilePhoto ? (
              <img src={profilePhoto || "/placeholder.svg"} alt="Profile" className="profile-image" />
            ) : (
              <div className="profile-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>Upload Photo</span>
              </div>
            )}
          </label>
          <input
            id="profile-upload"
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="profile-input"
          />
        </div>

        <div className="profile-actions">
          <p className="upload-hint">{ACCOUNT_CONSTANTS.MESSAGES.PHOTO_HINT}</p>

          {profilePhoto && (
            <div className="photo-buttons">
              <button
                type="button"
                onClick={handlePhotoSubmit}
                disabled={formState.loading}
                className="btn btn-primary"
              >
                {formState.loading ? <LoadingSpinner size="small" /> : "Save Photo"}
              </button>
              <button type="button" onClick={handleRemovePhoto} className="btn btn-secondary">
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      {formState.error && (
        <div className="form-message error">
          <span className="message-icon">⚠️</span>
          {formState.error}
        </div>
      )}

      {formState.success && (
        <div className="form-message success">
          <span className="message-icon">✅</span>
          {formState.success}
        </div>
      )}
    </div>
  )
}

export default ProfilePhotoUpload
